import React, { useRef, useEffect, useMemo, useState } from 'react';

// Styles & Assets
import styles from './Chat.module.css';
import errorIcon from '../../../assets/icons/error.svg';

// Components
import MessageChat from '../../UI/MessageChat/MessageChat';
import MediaDate from '../MediaDate/MediaDate';

// other
import moment from 'moment';
import Tooltip from '../../UI/Tooltip/Tooltip';

// Interfaces
import { IMessage, MessageDay } from '../../../interfaces/Chat';
import LineSeparator from '../../Helpers/LineSeparator/LineSeparator';

// Context
import { useChatContext } from '../../../context/ChatContext';

// Hooks
import { S3File } from '../../../hooks/useChatFiles';

// Language
import { useTranslation } from 'react-i18next';


interface Props {
    messages: IMessage[];
    userID: string;
    showMedia: boolean;
}

const Chat: React.FC<Props> = ({ messages, userID, showMedia }) => {
    const { listFiles, downloadFile, failedMessages } = useChatContext();
    const [mediaMessages, setMediaMessages] = useState<S3File[]>([]);
    const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
    
    const { t } = useTranslation();

     // Check if a message has failed
    const isMessageFailed = (msg: IMessage) => msg.status === 'failed' || failedMessages.some(fm => fm.id === msg.id);

    // group messages by day
    const groupByDay = (messages: IMessage[]): MessageDay[] => {
        const map: Record<string, IMessage[]> = {};
        messages.forEach(m => {
            const day = moment(m.timestamp).format('DD/MM/YYYY');
            (map[day] ??= []).push(m);
        });
        return Object.entries(map).map(([day, messages]) => ({ day, messages }));
    };

    // load media messages when in gallery mode
    useEffect(() => {
        if (!showMedia || !listFiles) return;
        let isMounted = true;
        const loadMedia = async () => {
            const files = await listFiles();
            if (!isMounted) return;
            setMediaMessages(files);
        };
        loadMedia();
        return () => { isMounted = false; };
    }, [showMedia, listFiles]);

    // download media URLs
    useEffect(() => {
        if (!downloadFile || mediaMessages.length === 0) return;
        let isMounted = true;
        const loadUrls = async () => {
            const urls: Record<string, string> = {};
            for (const file of mediaMessages) {
                const blob = await downloadFile(file.key);
                if (blob && isMounted) {
                    urls[file.key] = URL.createObjectURL(blob);
                }
            }
            if (isMounted) setMediaUrls(urls);
        };
        loadUrls();
        return () => { isMounted = false; };
    }, [mediaMessages, downloadFile]);

    useEffect(() => {
        if (showMedia) return; // only for chat mode
        if (!downloadFile) return;

        let isMounted = true;

        const loadMessageFiles = async () => {
            const urls: Record<string, string> = {};

            for (const msg of messages) {
                const key = msg.customMetadata?.attachments?.[0]?.s3Key;
                if (!key) continue;

                // Skip if already loaded
                if (mediaUrls[key]) continue;

                const blob = await downloadFile(key);
                if (blob && isMounted) {
                    urls[key] = URL.createObjectURL(blob);
                }
            }

            if (isMounted && Object.keys(urls).length > 0) {
                setMediaUrls(prev => ({ ...prev, ...urls }));
            }
        };

        loadMessageFiles();

        return () => { isMounted = false; };
    }, [messages, downloadFile, showMedia]);

    const mediaAsMessages: IMessage[] = useMemo(() => {
        const mappedFiles = mediaMessages.map(file => {
            let dataType: IMessage['dataType'] = 'DOCUMENT';
            if (file.name.match(/\.(png|jpg|jpeg)$/i)) dataType = 'IMAGE';
            else if (file.name.match(/\.(mp4)$/i)) dataType = 'VIDEO';
            
            return {
                id: file.key,
                sender: 'system',
                content: file.name,
                timestamp: file.lastModified.toISOString(),
                dataType,
                dataURL: mediaUrls[file.key] || '',
                customMetadata: {
                    attachments: [{
                        originalName: file.name,
                        mimeType: dataType,
                        size: file.size,
                        s3Key: file.key,
                    }]
                }
            } as IMessage;
        });

        return mappedFiles.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }, [mediaMessages, mediaUrls]);

    const enrichedMessages = useMemo(() => {
        const filteredMessages = messages.filter(msg => !msg.customMetadata?.isSystemMessage);

        return filteredMessages.map(msg => {
            const attachment = msg.customMetadata?.attachments?.[0];
            if (!attachment) return msg;

            let dataType: IMessage['dataType'] = "DOCUMENT";
            if (attachment.mimeType.startsWith("image/")) dataType = "IMAGE";
            else if (attachment.mimeType.startsWith("video/")) dataType = "VIDEO";

            return {
            ...msg,
            dataType,
            dataURL: mediaUrls[attachment.s3Key] || "",
            };
        });
    }, [messages, mediaUrls]);

    const chatToRender = useMemo(
        () => groupByDay(showMedia ? mediaAsMessages : enrichedMessages),
        [showMedia, mediaAsMessages, enrichedMessages]
    );

    const chatContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (showMedia) return;
        const container = chatContainerRef.current;
        if (!container) return;
        container.scrollTop = container.scrollHeight;
    }, [messages.length, showMedia]);

    // Preload image for it to be usable on offline mode when sending fails
    const errorIconImg = new Image();
    errorIconImg.src = errorIcon;

    return (
        <div ref={chatContainerRef} className={styles.chatContainer}>
            {showMedia && chatToRender.length === 0 && (
                <div className={styles.emptyState}>
                    {t('no_shared_files_in_chat')}
                </div>
            )}
            {chatToRender.map((chatDay, index) =>
                <div key={`${chatDay.day}-${index}`}>
                    <LineSeparator text={chatDay.day} />
                    {showMedia ? (
                        <MediaDate messages={chatDay.messages} />
                    ) : (
                        chatDay.messages.map((msg, idx) => {
                            const prev = chatDay.messages[idx - 1];
                            const next = chatDay.messages[idx + 1];
                            const isLocalMessage = msg.localId && msg.localId === msg.id;
                            const isMessageFromCarer = !!msg.customMetadata?.carerName;
                            const showSender = (!prev || prev.sender !== msg.sender) && !isLocalMessage || isMessageFromCarer;
                            const isFirst = showSender;
                            const isLast = !next || next.sender !== msg.sender;

                            const isFromUser = msg.senderType === 'user' || msg.senderType === 'system';

                            const isFailed = isMessageFailed(msg);

                            return (
                                <div key={`${msg.id}-${idx}`} className={styles.messageContainer}>
                                    {
                                        isFailed && isFromUser && 
                                        (
                                            <button className={styles.errorButton}>
                                                <Tooltip text={t('connection_issue_chat_unsent_message') as string}>
                                                    <img src={errorIconImg.src} alt="" />
                                                </Tooltip>
                                            </button>
                                        )
                                    }
                                    <MessageChat
                                        key={`${msg.timestamp}-${idx}`}
                                        msg={msg}
                                        user={isFromUser}
                                        customSender={msg.customMetadata?.carerName}
                                        showSender={showSender && (isFromUser || isMessageFromCarer)} // showSender only for users and carers, not patients
                                        isFirstInGroup={isFirst}
                                        isLastInGroup={isLast}
                                    />
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

export default Chat;